import {
  useGlobalConfigContext,
  usePTPConfigContext,
} from 'GlobalConfig.context';
import type PmParcel from 'interfaces/PmParcel';
import { StateUpdater, useState } from 'preact/hooks';
import { ptpCatchError } from 'services/sentry.services';
import { useTranslation } from 'react-i18next';
import { isRuntimeError } from 'utils/fetcher.utils';
import { useSearchParams } from 'hooks/useSearchParams';
import { getSearchFormConfigs } from 'services/globalConfig.services';
import { submitReturnRequest } from '../services';
import type { ReturnSubmissionFormData } from '../../../interfaces';

export const useConfirmCTA = (
  shipment: PmParcel,
  returnRequestFormData: ReturnSubmissionFormData,
  setApiError: StateUpdater<string>,
): {
  handleConfirmButton: () => void;
  isConfirming: boolean;
} => {
  const { updateSearchParams } = useSearchParams();
  const { organizationId, cmsSlug } = useGlobalConfigContext();
  const configs = usePTPConfigContext();
  const [isSubmitSucceed, setIsSubmitSucceed] = useState(false); // flag to check if return request has already submitted once
  const [isConfirming, setIsConfirming] = useState(false);
  const { t } = useTranslation();

  const handleConfirmButton = async () => {
    let encryptedReturnPk = '';
    try {
      if (!isSubmitSucceed) {
        setIsConfirming(true);
        encryptedReturnPk = await submitReturnRequest(
          organizationId,
          cmsSlug,
          shipment.poId,
          returnRequestFormData,
        );
        setIsSubmitSucceed(true);
      }

      const { searchFields } = getSearchFormConfigs(configs);

      // update url param
      const { urlParam: returnPkUrlParam } = searchFields.find(
        ({ key }) => key === 'return.id',
      );

      updateSearchParams({
        params: { pp_return: 'true', [returnPkUrlParam]: encryptedReturnPk },
        keysToRemove: searchFields.map((item) => item.urlParam),
      });
    } catch (error) {
      if (isRuntimeError(error)) {
        ptpCatchError(error);
      }
      setApiError(t(error.message));
    } finally {
      setIsConfirming(false);
    }
  };

  return { handleConfirmButton, isConfirming };
};
